import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 0;

/**
 * A custom element that manages a header menu (CLICK-BASED).
 *
 * - Click on a menu item toggles its submenu.
 * - Click outside closes the open submenu.
 * - Escape closes the open submenu.
 * - Keeps existing CSS vars: --submenu-height, --submenu-opacity
 * - Keeps existing data attrs: data-overflow-expanded, data-animating
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    // ✅ Click handling (delegated)
    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });

    // ✅ Close when clicking outside
    document.addEventListener('click', this.#onDocumentClick, {
      capture: true,
      signal: this.#abortController.signal,
    });

    // ✅ Close on Escape
    document.addEventListener('keydown', this.#onKeydown, { signal: this.#abortController.signal });

    // Optional: keep this if overflow panel should close when leaving it (click-based menus usually don't need it)
    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  // ---------------------------------------------------------------------------
  // Click-based interactions
  // ---------------------------------------------------------------------------

  /**
   * Handle click inside the header-menu (delegation)
   * - Toggles submenu for clicked item
   * - Prevents navigation on first click when a submenu exists
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Find the menu item for whatever was clicked
    const item = findMenuItem(target);
    if (!item) return;

    // If the click happened on a link, we may want to prevent navigation
    // to allow "first click opens submenu" behavior.
    const clickedLink = target.closest('a[href]');

    // Determine submenu (or overflow submenu)
    const { submenu, isOverflow } = this.#resolveSubmenuFromTarget(item, target);

    // If there is a submenu, first click should toggle it (not navigate).
    if (submenu && clickedLink) {
      event.preventDefault();
    }

    // Toggle
    if (this.#state.activeItem === item) {
      this.#deactivate(item);
    } else {
      this.#activateItem(item, submenu, isOverflow);
    }
  };

  /**
   * Close when clicking outside this component
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;

    const target = event.target;
    if (!(target instanceof Node)) return;

    // If click is inside header-menu, ignore
    if (this.contains(target)) return;

    // Otherwise close
    this.#deactivate();
  };

  /**
   * Close on Escape
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.#deactivate();
  };

  // ---------------------------------------------------------------------------
  // Activation / Deactivation (internal)
  // ---------------------------------------------------------------------------

  /**
   * Activate a specific item (no hover/focus needed)
   * @param {HTMLElement} item
   * @param {HTMLElement | null} submenu
   * @param {boolean} isOverflow
   */
  #activateItem = (item, submenu, isOverflow) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    // Keep the same event to allow other code to react
    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Remove active marker from previous submenu
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) delete prevSubmenu.dataset.active;
    }

    this.dataset.overflowExpanded = isOverflow.toString();

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    // Measure and set CSS vars
    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Used only for parity with original structure; keeps debounce API.
   */
  #activateHandler = () => {};
  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    // With click-based menus, you usually WANT it to close even if overflow is hovered.
    // If you want to keep the old behavior, uncomment next line:
    // if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay (kept for compatibility)
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Resolve submenu and whether this interaction should be treated as overflow.
   * @param {HTMLElement} item
   * @param {Element} rawTarget
   * @returns {{ submenu: HTMLElement | null, isOverflow: boolean }}
   */
  #resolveSubmenuFromTarget(item, rawTarget) {
    // If the click came from a slotted element that isn't in the default slot,
    // treat it as overflow (keeps original logic).
    const isDefaultSlot = rawTarget.slot === '';
    const isOverflow = !isDefaultSlot;

    let submenu = findSubmenu(item);
    if (!submenu && isOverflow) submenu = this.overflowMenu;

    return { submenu, isOverflow };
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // ✅ Fix selector bug + make it robust: use closest
  const more = element.closest?.('[slot="more"]');
  if (more) {
    // Select the first overflowing menu item when clicking the "More" item
    const firstOverflow = more.parentElement?.querySelector('[slot="overflow"]');
    return firstOverflow ? findMenuItem(firstOverflow) : null;
  }

  // Prefer closest menuitem, fallback to querySelector (keeps compatibility with your markup)
  const closestMenuItem = element.closest?.('[ref="menuitem"]');
  if (closestMenuItem instanceof HTMLElement) return closestMenuItem;

  const nested = element.querySelector?.('[ref="menuitem"]');
  return nested instanceof HTMLElement ? nested : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
